import { Button, Col, Divider, Row } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import StorageUnitContext from "../../../../context/wms/storage-unit/context";
import StorageUnitContextProvider from "../../../../context/wms/storage-unit/provider";
import {
  ICreateStorageUnit,
  IUpdateStorageUnit,
} from "../../../../models/wms/storage-unit/request";
import {
  StorageUnitType,
  PlacementSide,
  AisleLayout,
} from "../../../../models/wms/enums";
import EndPoints from "../../../../services/end-points";
import { execute } from "../../../../utils/helpers/execute";
import FieldBuilder from "../../../form-components/field-builder";
import Spin from "../../../general/antd/spin";
import styles from "./style.module.scss";
import useHierarchicalDropdown, {
  ChainItem,
} from "../../../../hooks/wms/use-hierarchical-dropdown";
import PreviousChainDisplay from "../../shared/previous-chain-display";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(StorageUnitContext);
  const { id } = useParams();

  // Helper to get translated label for sides value
  const getSidesLabel = (sides: number | undefined): string => {
    if (sides === undefined) return "";
    if (sides === AisleLayout.LEFT) return t("left_side");
    if (sides === AisleLayout.RIGHT) return t("right_side");
    if (sides === AisleLayout.BOTH) return t("both_sides");
    return `${sides}`;
  };

  const methods = useForm<any>({
    defaultValues: {
      aisleId: details?.aisleId || undefined,
      storageUnitType: details?.storageUnitType || undefined,
      side: details?.side || undefined,
      description: details?.description || "",
      pickSequence: details?.pickSequence || undefined,
      isActive: details?.isActive || false,
      shelvesCount: undefined as number | undefined,
      shelvesHaveBins: false,
    },
  });

  const { handleSubmit, reset, setValue, watch, getValues, trigger } = methods;

  const shelvesHaveBinsW = watch("shelvesHaveBins");

  useEffect(() => {
    if (shelvesHaveBinsW) {
      void trigger("shelvesCount");
    }
  }, [shelvesHaveBinsW, trigger]);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    aisles,
    loadingAisles,
    visibleLevels,
    previousChain,
    capturePreviousChain,
    clearPreviousChain,
    resetFromLevel,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedAisleId,
    selectedAisleLayout,
    selectedSidesFilter,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedAisleId,
    setSelectedSidesFilter,
    initializeForEdit,
  } = useHierarchicalDropdown({ debounceMs: 300 });

  // Watch form values for ancestor selectors
  const formStoreId = watch("storeId");
  const formZoneTypeId = watch("zoneTypeId");
  const formZoneId = watch("zoneId");
  const formAisleId = watch("aisleId");

  // Sync form values with hook selected values
  useEffect(() => {
    if (formStoreId !== selectedStoreId) {
      setSelectedStoreId(formStoreId);
    }
  }, [formStoreId, setSelectedStoreId]);

  useEffect(() => {
    if (formZoneTypeId !== selectedZoneTypeId) {
      setSelectedZoneTypeId(formZoneTypeId);
    }
  }, [formZoneTypeId, setSelectedZoneTypeId]);

  useEffect(() => {
    if (formZoneId !== selectedZoneId) {
      setSelectedZoneId(formZoneId);
    }
  }, [formZoneId, setSelectedZoneId]);

  useEffect(() => {
    if (formAisleId !== selectedAisleId) {
      setSelectedAisleId(formAisleId);
    }
  }, [formAisleId, selectedAisleId, setSelectedAisleId]);

  // Derive valid PlacementSide options from selected aisle's layout
  const sideOptions = useMemo(() => {
    const allOptions = [
      { value: PlacementSide.R, label: t("right_side") },
      { value: PlacementSide.L, label: t("left_side") },
    ];
    if (!selectedAisleLayout) return allOptions;
    if (selectedAisleLayout === AisleLayout.LEFT)
      return [{ value: PlacementSide.L, label: t("left_side") }];
    if (selectedAisleLayout === AisleLayout.RIGHT)
      return [{ value: PlacementSide.R, label: t("right_side") }];
    return allOptions; // BOTH
  }, [selectedAisleLayout, t]);

  // Auto-select side when only one option, reset when current value becomes invalid
  useEffect(() => {
    if (sideOptions.length === 1) {
      setValue("side", sideOptions[0].value);
    } else {
      const currentSide = watch("side");
      if (currentSide && !sideOptions.some((o) => o.value === currentSide)) {
        setValue("side", undefined);
      }
    }
  }, [sideOptions, setValue, watch]);

  // Capture previous chain on mount in edit mode
  useEffect(() => {
    if (id && details) {
      const chain: ChainItem[] = [];
      if (details.store?.name) {
        chain.push({ label: details.store.name, value: details.storeId });
      } else if (details.storeId) {
        chain.push({
          label: `Store ${details.storeId}`,
          value: details.storeId,
        });
      }
      if (details.zone?.name) {
        chain.push({ label: details.zone.name, value: details.zoneId });
      } else if (details.zoneId) {
        chain.push({ label: `Zone ${details.zoneId}`, value: details.zoneId });
      }
      if (details.aisle?.name) {
        const aisleLabel =
          details.aisle.sides !== undefined
            ? `${details.aisle.name} - ${t("sides")}: ${getSidesLabel(details.aisle.sides)}`
            : details.aisle.name;
        chain.push({ label: aisleLabel, value: details.aisleId });
      } else if (details.aisleId) {
        chain.push({
          label: `Aisle ${details.aisleId}`,
          value: details.aisleId,
        });
      }
      if (chain.length > 0) {
        capturePreviousChain(chain);
      }
    }
  }, [id, details]);

  // Handle store change - reset children
  const handleStoreChange = (storeId: number | undefined) => {
    if (storeId && storeId !== details?.storeId) {
      resetFromLevel("store");
      setValue("zoneId", undefined as any);
      setValue("aisleId", undefined as any);
      setValue("side", undefined as any);
    }
  };

  // Handle zone type change - reset children
  const handleZoneTypeChange = (zoneTypeId: number | undefined) => {
    if (zoneTypeId && zoneTypeId !== details?.zone?.zoneTypeId) {
      resetFromLevel("zone");
      setValue("zoneId", undefined as any);
      setValue("aisleId", undefined as any);
      setValue("side", undefined as any);
    }
  };

  // Handle zone change - reset children
  const handleZoneChange = (zoneId: number | undefined) => {
    if (zoneId && zoneId !== details?.zoneId) {
      resetFromLevel("zone");
      setValue("aisleId", undefined as any);
      setValue("side", undefined as any);
    }
  };

  // When details load, reset form and initialize hierarchical dropdowns immediately
  useEffect(() => {
    if (details) {
      reset({
        storeId: details.storeId ?? undefined,
        zoneTypeId: details.zone?.zoneTypeId ?? undefined,
        zoneId: details.zoneId ?? undefined,
        helperAisleSides: details.aisle?.sides ?? undefined,
        aisleId: details.aisleId ?? undefined,
        storageUnitType: details.storageUnitType ?? undefined,
        side: details.side ?? undefined,
        description: details.description ?? "",
        pickSequence: details.pickSequence ?? undefined,
        isActive: details.isActive ?? false,
      });
      initializeForEdit({
        storeId: details.storeId,
        zoneTypeId: details.zone?.zoneTypeId,
        zoneId: details.zoneId,
        aisleId: details.aisleId,
        sidesFilter: details.aisle?.sides,
      });
    }
  }, [details, reset, initializeForEdit]);

  const normalizePickSequence = (v: unknown): number | undefined => {
    if (v === "" || v === undefined || v === null) return undefined;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n) || n < 1) return undefined;
    return n;
  };

  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          const {
            storeId,
            zoneTypeId,
            zoneId,
            helperAisleSides,
            shelvesCount,
            shelvesHaveBins,
            pickSequence,
            ...rest
          } = data;
          const pickSeq = normalizePickSequence(pickSequence);
          const updateData: IUpdateStorageUnit = {
            ...rest,
            ...(pickSeq !== undefined ? { pickSequence: pickSeq } : {}),
          };
          await EndPoints.wmsLayout.updateStorageUnit(details.id, updateData);
        } else {
          const {
            storeId,
            zoneTypeId,
            zoneId,
            helperAisleSides,
            shelvesCount: shelvesCountRaw,
            shelvesHaveBins,
            pickSequence,
            ...createRest
          } = data;
          const shelvesCountNum =
            shelvesCountRaw === "" ||
            shelvesCountRaw === undefined ||
            shelvesCountRaw === null
              ? undefined
              : Number(shelvesCountRaw);
          const pickSeq = normalizePickSequence(pickSequence);
          const createData: ICreateStorageUnit = {
            ...createRest,
            ...(pickSeq !== undefined ? { pickSequence: pickSeq } : {}),
          };
          if (
            shelvesCountNum !== undefined &&
            Number.isFinite(shelvesCountNum) &&
            shelvesCountNum >= 1
          ) {
            createData.shelvesCount = Math.floor(shelvesCountNum);
            if (shelvesHaveBins === true) {
              createData.shelvesHaveBins = true;
            }
          }
          await EndPoints.wmsLayout.createStorageUnit(createData);
        }
        navigate("/wms/storage-units");
      },
      fallback: () => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_storage_unit") : t("add_storage_unit")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <>
              {previousChain && (
                <PreviousChainDisplay
                  chain={previousChain}
                  onClear={clearPreviousChain}
                />
              )}
              <div className={styles.children}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="storeId"
                      label={t("select_store")}
                      input={{
                        type: "select",
                        loading: loadingStores,
                        allowSearch: true,
                        options: stores.map((s) => ({
                          label: s.label,
                          value: s.id,
                        })),
                        onChange: handleStoreChange,
                      }}
                      rules={{ required: !details }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="zoneTypeId"
                      label={t("zone_type")}
                      input={{
                        type: "select",
                        loading: loadingZoneTypes,
                        allowSearch: true,
                        options: zoneTypes.map((zt) => ({
                          label: zt.label,
                          value: zt.id,
                        })),
                        onChange: handleZoneTypeChange,
                      }}
                      rules={{ required: false }}
                    />
                  </Col>
                  {visibleLevels.zone && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="zoneId"
                        label={t("select_zone")}
                        input={{
                          type: "select",
                          loading: loadingZones,
                          allowSearch: true,
                          options: zones.map((z) => ({
                            label: z.label,
                            value: z.id,
                          })),
                          onChange: handleZoneChange,
                        }}
                        rules={{ required: !details }}
                      />
                    </Col>
                  )}
                  {visibleLevels.aisle && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="helperAisleSides"
                        label={`${t("available_sides")}`}
                        input={{
                          type: "select",
                          options: [
                            { label: t("left_side"), value: 1 },
                            { label: t("right_side"), value: 2 },
                            { label: t("both_sides"), value: 3 },
                          ],
                          onChange: setSelectedSidesFilter,
                        }}
                        rules={{ required: false }}
                      />
                    </Col>
                  )}
                  {visibleLevels.aisle && (
                    <Col xs={24} md={12}>
                      <FieldBuilder
                        name="aisleId"
                        label={t("select_aisle")}
                        input={{
                          type: "select",
                          loading: loadingAisles,
                          allowSearch: true,
                          options: aisles.map((a) => ({
                            label:
                              a.sides !== undefined
                                ? `${a.label} - ${t("sides")}: ${getSidesLabel(a.sides)}`
                                : a.label,
                            value: a.id,
                          })),
                        }}
                        rules={{ required: true }}
                      />
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="storageUnitType"
                      label={t("storage_unit_type")}
                      input={{
                        type: "select",
                        options: [
                          {
                            value: StorageUnitType.CABINET,
                            label: t("storage_unit_type_cabinet"),
                          },
                          {
                            value: StorageUnitType.FRIDGE,
                            label: t("storage_unit_type_fridge"),
                          },
                          {
                            value: StorageUnitType.TABLE,
                            label: t("storage_unit_type_table"),
                          },
                        ],
                      }}
                      rules={{ required: true }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="side"
                      label={t("placement_side")}
                      input={{
                        type: "select",
                        options: sideOptions,
                      }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="pickSequence"
                      label={t("pick_sequence")}
                      input={{ type: "number" }}
                      rules={{
                        required: false,
                        validate: (v) =>
                          v === "" ||
                          v === undefined ||
                          v === null ||
                          Number(v) >= 1
                            ? true
                            : t("min", { input: t("pick_sequence"), value: 1 }),
                      }}
                    />
                  </Col>
                  {!details && (
                    <>
                      <Col xs={24} md={12}>
                        <FieldBuilder
                          name="shelvesCount"
                          label={t("shelves_count")}
                          input={{ type: "number" }}
                          rules={{
                            required: false,
                            validate: (v) => {
                              const bins = getValues("shelvesHaveBins");
                              if (bins) {
                                if (
                                  v === "" ||
                                  v === undefined ||
                                  v === null ||
                                  !Number.isFinite(Number(v)) ||
                                  Number(v) < 1
                                ) {
                                  return t(
                                    "wms_shelves_count_required_when_shelves_have_bins",
                                  );
                                }
                              } else if (
                                v !== "" &&
                                v !== undefined &&
                                v !== null
                              ) {
                                const n = Number(v);
                                if (!Number.isFinite(n) || n < 1) {
                                  return t("min", {
                                    input: t("shelves_count"),
                                    value: 1,
                                  });
                                }
                              }
                              return true;
                            },
                          }}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <FieldBuilder
                          name="shelvesHaveBins"
                          label={t("shelves_have_bins")}
                          input={{ type: "switch" }}
                          rules={{ required: false }}
                        />
                      </Col>
                    </>
                  )}
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="description"
                      label={t("description")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="isActive"
                      label={t("is_active")}
                      input={{ type: "switch" }}
                      rules={{ required: false }}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button onClick={() => navigate(-1)}>{t("cancel")}</Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const StorageUnitForm = () => {
  return (
    <StorageUnitContextProvider>
      <Form />
    </StorageUnitContextProvider>
  );
};

export default StorageUnitForm;
